import React from 'react'

import { Button, TextLink, UiSwapIcon } from '@octane/spark'
import FormContent from 'components/FormContent'
import FormSection from 'components/FormSection'
import FormValidationMessage from 'components/FormValidationMessage/FormValidationMessage'
import {
    FieldGroup,
    FieldInstructionBundle,
    FieldKit,
    Form,
    FormBuilder,
    FormBuilderProps,
    FormValues,
} from 'containers/FormBuilder'

import CASPartnerCopyWarning from '../../../components/CASPartnerCopyWarning'
import config from '../../../config'
import { generateFieldInstructionsWithPrefill } from '../utils'
import { WisconsinInstructionFields } from '../wisconsinInstructions'
import {
    WisconsinApplicantSection,
    WisconsinCoapplicantSection,
} from '../WisconsinMaritalPropertySection'
import { ReadOnlyFieldset } from './AutomobileFormBuilder.styled'

export const swapApplicantsTextLinkText = `Swap Primary and Co-applicants`

export type AutomobileSubmissionFormInstructions = {
    financeManagerFields: FieldInstructionBundle
    collateralFields: FieldInstructionBundle
    tradeInFields: FieldInstructionBundle
    applicantInformationFields: FieldInstructionBundle
    applicantResidencyFields: FieldInstructionBundle
    employmentFields: FieldInstructionBundle
    coapplicantInformationFields1: FieldInstructionBundle
    coapplicantAddressFields: FieldInstructionBundle
    coapplicantInformationFields2: FieldInstructionBundle
    coapplicantResidencyFields: FieldInstructionBundle
    coapplicantEmploymentFields: FieldInstructionBundle
    applicationTypeFields: FieldInstructionBundle
    relationOfCoapplicantFields: FieldInstructionBundle
    consentFields: FieldInstructionBundle
    verificationFields: FieldInstructionBundle
} & WisconsinInstructionFields

type FormValuesToBoolFunc = (formValues: FormValues) => boolean

export type AutomobileFormBuilderProps = {
    instructions: AutomobileSubmissionFormInstructions
    collateralType: string
    isApplicationJointFunc: FormValuesToBoolFunc
    swapApplicantsFunc: (fieldKit: FieldKit) => void
    isCoapplicantAddressSameFunc: FormValuesToBoolFunc
    overrideDefaultInitialValues?: FormBuilderProps['overrideDefaultInitialValues']
    enableReinitialize?: FormBuilderProps['enableReinitialize']
    onSubmit: FormBuilderProps['onSubmit']
    sectionWidth?: string
    sectionMargin?: string
    isCopy?: boolean
    partner?: string
    canSubmit?: boolean
    disclosureLoadFailed?: boolean
    isReadOnly?: boolean
}

export default function AutomobileFormBuilder(
    props: AutomobileFormBuilderProps,
): JSX.Element {
    const {
        instructions,
        collateralType,
        isApplicationJointFunc,
        swapApplicantsFunc,
        isCoapplicantAddressSameFunc,
        overrideDefaultInitialValues,
        enableReinitialize,
        onSubmit,
        sectionWidth,
        sectionMargin,
        isCopy,
        partner,
        canSubmit,
        disclosureLoadFailed,
        isReadOnly,
    } = props

    let allInstructions = instructions

    if (isReadOnly) {
        allInstructions = Object.keys(allInstructions).reduce(
            (acc: AutomobileSubmissionFormInstructions, key: string) => {
                const bundleName =
                    key as keyof AutomobileSubmissionFormInstructions

                const innerBundle = allInstructions[bundleName]

                if (innerBundle) {
                    acc[bundleName] =
                        generateFieldInstructionsWithPrefill(innerBundle)
                }

                return acc
            },
            {} as AutomobileSubmissionFormInstructions,
        )
    }

    const combinedFieldsBundle: FieldInstructionBundle = Object.values(
        allInstructions,
    ).reduce<FieldInstructionBundle>((acc, instructionBundle) => {
        return {
            ...acc,
            ...instructionBundle,
        }
    }, {})

    const autoCompleteSetting = config.ENVIRONMENT === 'local' ? 'on' : 'off'

    return (
        <FormBuilder
            fieldInstructionBundle={combinedFieldsBundle}
            onSubmit={onSubmit}
            overrideDefaultInitialValues={overrideDefaultInitialValues || {}}
            enableReinitialize={enableReinitialize}
        >
            {({ fieldKit, formKit }) => {
                return (
                    <Form autoComplete={autoCompleteSetting}>
                        <ReadOnlyFieldset disabled={isReadOnly}>
                            <CASPartnerCopyWarning
                                isCopy={isCopy}
                                partner={partner}
                                sectionMargin={sectionMargin}
                                sectionWidth={sectionWidth}
                            />
                            {/* FINANCE MANAGER */}
                            <FormSection
                                title=""
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FormContent gridTemplate="auto minmax(auto, 700px) auto">
                                    <div />
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.financeManagerFields
                                        }
                                    />
                                    <div />
                                </FormContent>
                            </FormSection>

                            {/* COLLATERAL FIELDS */}
                            <FormSection
                                title="Collateral Information"
                                subtitle={collateralType}
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.collateralFields
                                    }
                                />
                            </FormSection>

                            <FormSection
                                title=""
                                subtitle=""
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.tradeInFields
                                    }
                                />
                            </FormSection>

                            {/* APPLICANT FIELDS */}
                            <FormSection
                                title="Applicant Information"
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FormContent gridTemplate="3fr 2fr">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.applicationTypeFields
                                        }
                                    />
                                    {isApplicationJointFunc(
                                        fieldKit.values,
                                    ) && (
                                        <TextLink
                                            id="swapApplicantsTextLink"
                                            data-testid="swapApplicantsTextLink"
                                            startIcon={UiSwapIcon}
                                            size="medium"
                                            disabled={false}
                                            style={{
                                                marginTop: '1rem',
                                                textAlign: 'left',
                                            }}
                                            onClick={() => {
                                                swapApplicantsFunc(fieldKit)
                                            }}
                                        >
                                            {swapApplicantsTextLinkText}
                                        </TextLink>
                                    )}
                                </FormContent>

                                <FormContent gridTemplate="repeat(24, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.applicantInformationFields
                                        }
                                    />
                                </FormContent>

                                <FormContent gridTemplate="repeat(24, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.applicantResidencyFields
                                        }
                                    />
                                </FormContent>

                                <h3>Employment</h3>
                                <FormContent gridTemplate="repeat(24, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.employmentFields
                                        }
                                    />
                                </FormContent>

                                <WisconsinApplicantSection
                                    fieldKit={fieldKit}
                                    instructions={allInstructions}
                                />
                            </FormSection>

                            {/* CO-APPLICANT FIELDS */}
                            {isApplicationJointFunc(fieldKit.values) && (
                                <FormSection
                                    title="Co-Applicant Information"
                                    width={sectionWidth}
                                    margin={sectionMargin}
                                >
                                    <FormContent gridTemplate="2fr 1fr">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.relationOfCoapplicantFields
                                            }
                                        />
                                    </FormContent>

                                    <FormContent gridTemplate="repeat(24, 1fr)">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.coapplicantInformationFields1
                                            }
                                        />
                                    </FormContent>

                                    {!isCoapplicantAddressSameFunc(
                                        fieldKit.values,
                                    ) && (
                                        <FormContent gridTemplate="repeat(24, 1fr)">
                                            <FieldGroup
                                                fieldKit={fieldKit}
                                                fieldInstructionBundle={
                                                    allInstructions.coapplicantAddressFields
                                                }
                                            />
                                        </FormContent>
                                    )}

                                    <FormContent gridTemplate="repeat(24, 1fr)">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.coapplicantInformationFields2
                                            }
                                        />
                                    </FormContent>

                                    <FormContent gridTemplate="repeat(24, 1fr)">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.coapplicantResidencyFields
                                            }
                                        />
                                    </FormContent>

                                    <h3>Co-Applicant Employment</h3>
                                    <FormContent gridTemplate="repeat(24, 1fr)">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.coapplicantEmploymentFields
                                            }
                                        />
                                    </FormContent>

                                    <WisconsinCoapplicantSection
                                        fieldKit={fieldKit}
                                        instructions={allInstructions}
                                    />
                                </FormSection>
                            )}

                            <fieldset
                                disabled={disclosureLoadFailed}
                                style={
                                    disclosureLoadFailed
                                        ? {
                                              opacity: 0.4,
                                              border: 'none',
                                              padding: 0,
                                              margin: 0,
                                          }
                                        : {
                                              border: 'none',
                                              padding: 0,
                                              margin: 0,
                                          }
                                }
                                data-testid="consent-sections"
                            >
                                <FormSection
                                    title=""
                                    subtitle=""
                                    width={sectionWidth}
                                    margin={sectionMargin}
                                >
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.consentFields
                                        }
                                    />
                                </FormSection>
                                <FormSection
                                    title=""
                                    subtitle=""
                                    width={sectionWidth}
                                    margin={sectionMargin}
                                >
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.verificationFields
                                        }
                                    />
                                </FormSection>
                            </fieldset>
                            {!isReadOnly && (
                                <FormSection
                                    title=""
                                    width={sectionWidth}
                                    margin={sectionMargin}
                                >
                                    <FormContent gridTemplate="1fr">
                                        <FormValidationMessage
                                            message={`
                                            Please address all validation messages
                                            in the form before submitting this
                                            application
                                        `}
                                            formIsValid={formKit.isValid}
                                            submitAttempted={
                                                formKit.submitCount > 0
                                            }
                                        />
                                        <Button
                                            type="submit"
                                            id="instantDecisionButton"
                                            size="large"
                                            disabled={
                                                !canSubmit ||
                                                formKit.isSubmitting
                                            }
                                            data-testid="instantDecisionButton"
                                        >
                                            Advance to Instant Decision
                                        </Button>
                                    </FormContent>
                                </FormSection>
                            )}
                        </ReadOnlyFieldset>
                    </Form>
                )
            }}
        </FormBuilder>
    )
}

AutomobileFormBuilder.defaultProps = {
    overrideDefaultInitialValues: undefined,
    enableReinitialize: undefined,
    sectionWidth: undefined,
    sectionMargin: undefined,
    isCopy: false,
    partner: '',
    canSubmit: true,
}
